import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST } from '../../../../../constants/common'
import { RENTAL_VIEWING_ENTITY } from '../../../../../constants/entity'
import { useSelection } from '../../../../../hooks'
import { useLocationPagination } from '../../../../Pagination'
import {
  getMockViewingsPage,
  getViewingsColumns,
} from '../../../../RentalPortalViewing'
import {
  VIEWING_REQUIRED_COLUMNS,
  VIEWINGS_PAGE_SIZE,
} from '../../RentalPortalViewings.constants'

import { EMPTY_ITEMS } from './useRentalPortalViewingsMockTable.constants'
import {
  getViewingsFiltersKey,
  getViewingsFilterValuesFromKey,
} from './useRentalPortalViewingsMockTable.utils'

export function useRentalPortalViewingsMockTable({ filterValues }) {
  const [columns, setColumns] = useState(getViewingsColumns)
  const [ordering, setOrdering] = useState(EMPTY_LIST)
  const [pageSize, setPageSize] = useState(VIEWINGS_PAGE_SIZE)

  const [isConfigOpen, setConfigOpen] = useState(false)
  const [preview, setPreview] = useState(null)

  const [reloadKey, setReloadKey] = useState(0)

  const [response, setResponse] = useState(null)
  const [serverError, setServerError] = useState(null)

  const [isInitiated, setInitiated] = useState(false)
  const [isLoadingPage, setLoadingPage] = useState(true)

  const readIdRef = useRef(0)

  const prevReadRef = useRef(null)

  const pageScrollWrapperRef = useRef(null)

  const filtersKey = getViewingsFiltersKey(filterValues)

  const { pagination: locationPagination, setPagination } =
    useLocationPagination()

  const page = locationPagination.page

  const items = response?.items ?? EMPTY_ITEMS

  const pagination = response?.pagination ?? {
    count: 0,
    page,
    pageCount: 1,
    pageSize,
  }

  const selection = useSelection({ data: items, pagination })

  useEffect(() => {
    readIdRef.current += 1

    const readId = readIdRef.current

    const prevRead = prevReadRef.current

    const isPageTurn =
      prevRead !== null &&
      prevRead.filtersKey === filtersKey &&
      prevRead.ordering === ordering &&
      prevRead.reloadKey === reloadKey &&
      (prevRead.page !== page || prevRead.pageSize !== pageSize)

    prevReadRef.current = { filtersKey, ordering, page, pageSize, reloadKey }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- the in-flight flag flips when the read starts
    setLoadingPage(true)

    getMockViewingsPage({
      filterValues: getViewingsFilterValuesFromKey(filtersKey),
      isInitial: readId === 1,
      ordering,
      page,
      pageSize,
    })
      .then(nextResponse => {
        if (readIdRef.current === readId) {
          setResponse(nextResponse)
          setServerError(null)
          setPagination(nextResponse.pagination, { replace: true })

          if (isPageTurn) {
            pageScrollWrapperRef.current?.scrollTo({
              behavior: 'smooth',
              top: 0,
            })
          }
        }
      })
      .catch(error => {
        if (readIdRef.current === readId) {
          setServerError(error)
        }
      })
      .finally(() => {
        if (readIdRef.current === readId) {
          setLoadingPage(false)
          setInitiated(true)
        }
      })
  }, [filtersKey, ordering, page, pageSize, reloadKey])

  async function handleSetOrdering(nextOrdering) {
    setOrdering(nextOrdering)
    setPagination({ ...locationPagination, page: 1 })

    return nextOrdering
  }

  async function handleResetOrdering() {
    return handleSetOrdering(EMPTY_LIST)
  }

  function handleSetSize(nextSize) {
    setPageSize(nextSize)
    setPagination({ ...locationPagination, page: 1 })
  }

  async function handleRefresh() {
    selection.handleCleanSelection()
    setReloadKey(prevReloadKey => prevReloadKey + 1)
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  async function handleApply() {}

  async function handleUpdateConfig() {}

  return {
    ...selection,
    allItems: response?.allItems ?? EMPTY_LIST,
    columns,
    entity: RENTAL_VIEWING_ENTITY,
    handleApply,
    handleConfigOpen,
    handleRefresh,
    handleResetOrdering,
    handleSetColumns: setColumns,
    handleSetOrdering,
    handleSetPagination: setPagination,
    handleSetPreview: setPreview,
    handleSetSize,
    handleUpdateConfig,
    isConfigOpen,
    isLoading: !isInitiated,
    isRefetching: isLoadingPage && isInitiated,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions: items.permissions,
    preview,
    requiredColumns: VIEWING_REQUIRED_COLUMNS,
    serverError,
    setConfigOpen,
  }
}
